<template>
  <div class="space-y-4 p-4 max-w-2xl mx-auto">
    <div
      v-for="(item, index) in activities"
      :key="item.id"
    >
      <div
        v-if="index > 0 && itemDay(item) !== itemDay(activities[index - 1])"
        class="relative flex items-center my-6"
      >
        <div class="flex-grow border-t border-gray-200" />
        <span class="mx-3 text-xs font-semibold text-gray-500 whitespace-nowrap">{{ formatDay(item) }}</span>
        <div class="flex-grow border-t border-gray-200" />
      </div>
      <!-- POST -->
      <div
        v-if="item.type === 'POST'"
        class="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm"
      >
        <div class="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <img
            v-if="logoUrl(item.company)"
            :src="logoUrl(item.company)"
            class="h-9 w-auto flex-shrink-0 object-contain"
            style="max-width: 100px"
            alt=""
          >
          <div
            v-else
            class="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-indigo-100 flex items-center justify-center"
          >
            <span class="text-indigo-600 font-bold text-sm">{{ item.company.name[0] }}</span>
          </div>
          <div class="flex-1 min-w-0">
            <a
              :href="`/companies/${item.company.id}`"
              target="_blank"
              rel="noopener noreferrer"
              class="font-semibold text-gray-900 hover:underline text-sm"
            >{{ item.company.name }}</a>
            <div class="text-xs text-gray-400">
              <template v-if="item.scheduledAt">
                Scheduled: {{ formatDate(item.scheduledAt) }}
              </template>
              <template v-else>
                {{ formatDate(item.createdAt) }}
              </template>
            </div>
          </div>
          <span
            v-if="item.company && item.company.productType"
            class="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
          >{{ item.company.productType }}</span>
          <span
            v-if="item.isBatch"
            class="bg-gray-100 text-gray-500 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
          >Batch</span>
          <span class="bg-indigo-100 text-indigo-600 text-xs font-semibold px-2 py-0.5 rounded-full uppercase flex-shrink-0">Post</span>
        </div>
        <template v-if="item.post">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div
            class="px-4 py-3 text-sm text-gray-700 leading-relaxed"
            v-html="(item.post.text || '').replace(/\n/g, '<br>')"
          />
          <video
            v-if="item.post.imagePath && isVideo(item.post.imagePath)"
            :src="item.post.imagePath"
            class="w-full"
            style="aspect-ratio:1/1;object-fit:cover;"
            controls
          />
          <img
            v-else-if="item.post.imagePath"
            :src="item.post.imagePath"
            class="w-full"
            style="aspect-ratio:1/1;object-fit:cover;"
            alt=""
          >
          <div
            v-if="item.post.socialMediaPosts && item.post.socialMediaPosts.length"
            class="flex flex-wrap gap-3 px-4 py-2 border-t border-gray-100 bg-gray-50"
          >
            <template v-for="sp in item.post.socialMediaPosts">
              <!-- failed -->
              <span
                v-if="sp.hasFailed"
                :key="sp.type + '-failed'"
                :title="sp.failureReason || 'Failed'"
                class="flex items-center gap-1.5 text-xs font-medium text-red-600"
              >
                <img
                  :src="PlatformIcon[sp.type]"
                  class="w-5 h-5 rounded opacity-60"
                  alt=""
                >
                {{ PlatformLabel[sp.type] }}: Failed
                <span
                  v-if="sp.failureReason"
                  class="text-red-400 font-normal"
                >({{ sp.failureReason }})</span>
              </span>
              <!-- pending -->
              <span
                v-else-if="!sp.completedAt && !sp.permalink"
                :key="sp.type + '-pending'"
                class="flex items-center gap-1.5 text-xs font-medium text-gray-400"
              >
                <img
                  :src="PlatformIcon[sp.type]"
                  class="w-5 h-5 rounded opacity-40"
                  alt=""
                >
                {{ PlatformLabel[sp.type] }}: Pending
              </span>
              <!-- published -->
              <a
                v-else
                :key="sp.type"
                :href="sp.permalink"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-1.5 text-xs font-medium"
                :style="{ color: PlatformColor[sp.type] }"
              >
                <img
                  :src="PlatformIcon[sp.type]"
                  class="w-5 h-5 rounded"
                  alt=""
                >
                View on {{ PlatformLabel[sp.type] }} ↗
              </a>
            </template>
          </div>
        </template>
      </div>

      <!-- INVITATION -->
      <div
        v-else-if="item.type === 'INVITATION'"
        class="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm"
      >
        <div class="flex items-center gap-3 px-4 py-3 border-b border-green-100 bg-green-50">
          <img
            v-if="logoUrl(item.company)"
            :src="logoUrl(item.company)"
            class="h-9 w-auto flex-shrink-0 object-contain"
            style="max-width: 100px"
            alt=""
          >
          <div
            v-else
            class="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-green-100 flex items-center justify-center"
          >
            <span class="text-green-700 font-bold text-sm">{{ item.company.name[0] }}</span>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-xs text-gray-500 uppercase tracking-wide">
              Invitation
            </div>
            <a
              :href="`/companies/${item.company.id}`"
              target="_blank"
              rel="noopener noreferrer"
              class="font-semibold text-gray-900 hover:underline text-sm"
            >{{ item.company.name }}</a>
          </div>
          <span
            v-if="item.company && item.company.productType"
            class="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
          >{{ item.company.productType }}</span>
          <span
            v-if="item.isBatch"
            class="bg-gray-100 text-gray-500 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
          >Batch</span>
          <span class="text-xs text-gray-400 flex-shrink-0">{{ formatDate(item.createdAt) }}</span>
        </div>
        <div class="px-4 py-3">
          <template v-if="item.invitation">
            <div class="flex items-center gap-2 mb-3">
              <span class="text-sm font-semibold text-gray-700">An:</span>
              <span class="bg-green-100 text-green-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                {{ item.invitation.leadsCount }} leads
              </span>
            </div>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div
              class="text-sm text-gray-700 leading-relaxed border-l-4 border-green-300 pl-3"
              v-html="(item.invitation.text || '').replace(/\n/g, '<br>')"
            />
          </template>
        </div>
      </div>

      <!-- MARKETING_TOPIC -->
      <div
        v-else-if="item.type === 'MARKETING_TOPIC'"
        class="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm"
      >
        <div class="flex items-center gap-3 px-4 py-3 border-b border-yellow-100 bg-yellow-50">
          <img
            v-if="logoUrl(item.company)"
            :src="logoUrl(item.company)"
            class="h-9 w-auto flex-shrink-0 object-contain"
            style="max-width: 100px"
            alt=""
          >
          <div
            v-else
            class="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-yellow-100 flex items-center justify-center"
          >
            <span class="text-yellow-700 font-bold text-sm">{{ item.company.name[0] }}</span>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-xs text-gray-500 uppercase tracking-wide">
              Marketing Topic
            </div>
            <a
              :href="`/companies/${item.company.id}`"
              target="_blank"
              rel="noopener noreferrer"
              class="font-semibold text-gray-900 hover:underline text-sm"
            >{{ item.company.name }}</a>
          </div>
          <span
            v-if="item.company && item.company.productType"
            class="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
          >{{ item.company.productType }}</span>
          <span
            v-if="item.marketingTopic && item.marketingTopic.isAiGenerated"
            class="bg-purple-100 text-purple-700 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
          >AI</span>
          <span
            v-else-if="item.marketingTopic"
            class="bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
          >Manual</span>
          <span class="text-xs text-gray-400 flex-shrink-0">{{ formatDate(item.createdAt) }}</span>
        </div>
        <div class="px-4 py-3">
          <template v-if="item.marketingTopic">
            <div class="font-bold text-gray-900 text-sm mb-1">
              {{ item.marketingTopic.title }}
            </div>
            <div class="text-sm text-gray-600 leading-relaxed">
              {{ item.marketingTopic.description }}
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- sentinel triggers next page load -->
    <div
      v-show="hasMore"
      ref="sentinel"
      class="h-4"
    />

    <div
      v-if="loadingMore"
      class="flex justify-center py-4"
    >
      <span class="text-gray-400 text-sm">Loading...</span>
    </div>
  </div>
</template>

<script>
import GET_ALL_ACTIVITIES from './getAllActivities.gql'

const PlatformIcon = Object.freeze({
  FACEBOOK: '/img/kindTypes/facebook.png',
  INSTAGRAM: '/img/kindTypes/instagram.png',
  LINKEDIN: '/img/kindTypes/linkedin.png'
})

const PlatformLabel = Object.freeze({
  FACEBOOK: 'Facebook',
  INSTAGRAM: 'Instagram',
  LINKEDIN: 'LinkedIn'
})

const PlatformColor = Object.freeze({
  FACEBOOK: '#1877f2',
  INSTAGRAM: '#e1306c',
  LINKEDIN: '#0a66c2'
})

export default {
  props: {
    filterPlatform: { type: String, default: null },
    filterCompanyId: { type: String, default: null },
    filterType: { type: String, default: null },
    scheduled: { type: Boolean, default: false },
    pollIntervalMin: { type: Number, default: 5 }
  },
  data () {
    return {
      // vue-apollo anchor key — actual data is populated via the result() hook
      activitiesPage: null,
      activities: [],
      currentPage: 1,
      total: 0,
      loadingMore: false,
      observer: null,
      lastFilterKey: null,
      PlatformIcon,
      PlatformLabel,
      PlatformColor
    }
  },
  computed: {
    hasMore () {
      return this.activities.length < this.total
    },
    queryInput () {
      const input = { page: 1, perPage: 10, scheduled: this.scheduled }
      if (this.filterPlatform) input.platform = this.filterPlatform
      if (this.filterCompanyId) input.companyIds = [this.filterCompanyId]
      if (this.filterType) input.type = this.filterType
      return input
    }
  },
  watch: {
    pollIntervalMin (val) {
      this.$apollo.queries.activitiesPage.startPolling(val * 60000)
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && this.hasMore && !this.loadingMore && this.activities.length > 0) {
          this.loadMore()
        }
      })
      if (this.$refs.sentinel) {
        this.observer.observe(this.$refs.sentinel)
      }
    })
  },
  beforeDestroy () {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    refresh () {
      this.$apollo.queries.activitiesPage.refetch()
    },
    isVideo (path) {
      return /\.(mp4|webm|mov|ogg)(\?.*)?$/i.test(path)
    },
    logoUrl (company) {
      return company?.images?.logo?.url || null
    },
    itemDay (item) {
      const d = item.scheduledAt || item.createdAt
      return new Date(d).toDateString()
    },
    formatDay (item) {
      const d = item.scheduledAt || item.createdAt
      return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
    },
    formatDate (dateStr) {
      return new Date(dateStr).toLocaleString()
    },
    async loadMore () {
      this.loadingMore = true
      try {
        const { data } = await this.$apollo.query({
          query: GET_ALL_ACTIVITIES,
          variables: { input: { ...this.queryInput, page: this.currentPage + 1 } },
          fetchPolicy: 'no-cache'
        })
        if (data?.activitiesPage) {
          this.activities = [...this.activities, ...data.activitiesPage.activities]
          this.currentPage++
          this.total = data.activitiesPage.total
        }
      } finally {
        this.loadingMore = false
      }
    }
  },
  apollo: {
    activitiesPage: {
      query: GET_ALL_ACTIVITIES,
      fetchPolicy: 'no-cache',
      pollInterval: 300000,
      variables () {
        return { input: this.queryInput }
      },
      result ({ data }) {
        if (!data?.activitiesPage) return
        const currentKey = JSON.stringify(this.queryInput)
        if (currentKey !== this.lastFilterKey) {
          this.activities = data.activitiesPage.activities
          this.currentPage = 1
          this.total = data.activitiesPage.total
          this.lastFilterKey = currentKey
        } else {
          const existingIds = new Set(this.activities.map(a => a.id))
          const newItems = data.activitiesPage.activities.filter(a => !existingIds.has(a.id))
          if (newItems.length > 0) {
            this.activities = [...newItems, ...this.activities]
            this.total = data.activitiesPage.total
          }
        }
      }
    }
  }
}
</script>
